import React, { useState, useEffect } from 'react';
import './App.css';
import './KPITheme.css';
import LoginAPI from './components/LoginAPI';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import Admin from './components/Admin';
import HR from './components/HR';
import UzupelnijRaport from './components/KPI/UzupelnijRaport';
import KPILayout from './components/KPI/KPILayout';
import KPIDashboard from './components/KPI/KPIDashboard';
import KPIWidget from './components/KPI/KPIWidget';
import PS from './components/KPI/PS';
import Transport from './components/KPI/Transport';
import Pick from './components/KPI/Pick';
import ForklifOww from './components/KPI/ForklifOww';
import Przyjecie from './components/KPI/Przyjecie';
import Inspekt from './components/KPI/Inspekt';
import KPIPodsumowanie from './components/KPI/KPIPodsumowanie';
import Tablica from './components/Quality/Tablica';
import PermissionsManager from './components/PermissionsManager';
import PermissionsGrid from './components/PermissionsGrid';
import DatabaseViewer from './components/DatabaseViewer';
import { 
  hasMenuAccess, 
  hasDashboardPermission, 
  hasQualityPermission, 
  hasHRPermission, 
  hasAdminPermission, 
  hasDatabasePermission,
  hasKpiPermission
} from './utils/permissions';
import usersAPI from './api/users';
import projectsAPI from './api/projects';
import shiftsAPI from './api/shifts';
import contractsAPI from './api/contracts';
import positionsAPI from './api/positions';
import employeesAPI from './api/employees';
import authAPI from './api/auth';

// Domyślne dane użytkowników (bez haseł - hasła powinny być zarządzane przez backend)
// W trybie demo, użytkownicy mogą logować się z dowolnymi danymi
const defaultUsers = [
  { email: 'superadmin@mcg.pl', name: 'Owner', role: 'owner', projects: [] },
  { email: 'admin@mcg.pl', name: 'Administrator', role: 'admin', projects: [] },
  { email: 'lider@mcg.pl', name: 'Lider Zespołu', role: 'lider', projects: ['Projekt A'] },
  { email: 'jan.kowalski@mcg.pl', name: 'Jan Kowalski', role: 'user', projects: ['Projekt A'] },
  { email: 'anna.nowak@mcg.pl', name: 'Anna Nowak', role: 'user', projects: ['Projekt B', 'Projekt C'] }
];

const defaultProjects = [
  'Projekt A',
  'Projekt B',
  'Projekt C',
  'Projekt D'
];

// Struktura zmian: { project: 'Projekt A', shifts: ['A', 'B', 'C'] }
const defaultShifts = {
  'Projekt A': ['A', 'B', 'C'],
  'Projekt B': ['A', 'B', 'C'],
  'Projekt C': ['A', 'B', 'C'],
  'Projekt D': ['A', 'B', 'C']
};

// Struktura umów: { project: 'Projekt A', contracts: ['Umowa o pracę', 'Umowa zlecenie', 'Umowa o dzieło'] }
const defaultContracts = {
  'Projekt A': ['Umowa o pracę', 'Umowa zlecenie', 'Umowa o dzieło'],
  'Projekt B': ['Umowa o pracę', 'Umowa zlecenie', 'Umowa o dzieło'],
  'Projekt C': ['Umowa o pracę', 'Umowa zlecenie', 'Umowa o dzieło'],
  'Projekt D': ['Umowa o pracę', 'Umowa zlecenie', 'Umowa o dzieło']
};

// Struktura stanowisk: { project: 'Projekt A', positions: ['Operator', 'Technolog', 'Kierownik'] }
const defaultPositions = {
  'Projekt A': ['Operator', 'Technolog', 'Kierownik', 'Specjalista'],
  'Projekt B': ['Operator', 'Technolog', 'Kierownik', 'Specjalista'],
  'Projekt C': ['Operator', 'Technolog', 'Kierownik', 'Specjalista'],
  'Projekt D': ['Operator', 'Technolog', 'Kierownik', 'Specjalista']
};

// Funkcje pomocnicze usunięte - teraz używamy API

function App() {
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentView, setCurrentView] = useState(() => {
    // Wczytaj zapisaną ścieżkę z localStorage
    const savedView = localStorage.getItem('currentView');
    return savedView || 'dashboard';
  });
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shifts, setShifts] = useState({});
  const [contracts, setContracts] = useState({});
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Otwórz konkretny alert w Quality (np. z linku w powiadomieniu)
  const [openAlertIdForQuality, setOpenAlertIdForQuality] = useState(null);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const handleNavigate = (event) => {
      const detail = event.detail;
      if (typeof detail === 'string') {
        setCurrentView(detail);
        setOpenAlertIdForQuality(null);
      } else {
        setCurrentView(detail.view);
        setOpenAlertIdForQuality(detail.openAlertId != null ? detail.openAlertId : null);
      }
      localStorage.setItem('currentView', typeof detail === 'string' ? detail : detail.view);
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    window.addEventListener('navigate', handleNavigate);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    // Sprawdź początkowy stan fullscreen
    handleFullscreenChange();
    
    return () => {
      window.removeEventListener('navigate', handleNavigate);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [currentView]);

  // Wczytaj użytkownika z localStorage i zweryfikuj token po odświeżeniu strony
  useEffect(() => {
    const initializeUser = async () => {
      setIsCheckingAuth(true);
      
      // Najpierw szybko sprawdź localStorage (synchronicznie)
      const savedUser = authAPI.getCurrentUser();
      const hasToken = authAPI.isAuthenticated();
      
      // Jeśli mamy zapisanego użytkownika i token, ustaw go natychmiast
      if (savedUser && hasToken) {
        // Upewnij się, że permissions jest obiektem
        const userWithPermissions = {
          ...savedUser,
          permissions: savedUser.permissions || {}
        };
        setUser(userWithPermissions);
        // NIE kończ tutaj - zweryfikuj token w tle
      } else if (!hasToken) {
        // Jeśli nie ma tokenu, nie ma użytkownika
        setUser(null);
        setIsCheckingAuth(false);
        return;
      }
      
      // Potem zweryfikuj token asynchronicznie (w tle)
      if (hasToken) {
        try {
          // Zweryfikuj token
          const verified = await authAPI.verify();
          if (verified && verified.user) {
            // Aktualizuj użytkownika z serwera (może mieć nowe dane, w tym uprawnienia)
            // Upewnij się, że permissions jest obiektem
            const userWithPermissions = {
              ...verified.user,
              permissions: verified.user.permissions || {}
            };
            setUser(userWithPermissions);
            // Zaktualizuj również localStorage, aby mieć aktualne uprawnienia
            localStorage.setItem('user', JSON.stringify(userWithPermissions));
          } else {
            // Jeśli weryfikacja nie powiodła się, ale mamy zapisanego użytkownika
            // zachowaj go (może być problem z siecią, nie z tokenem)
            if (!savedUser) {
              // Jeśli nie ma zapisanego użytkownika, wyloguj
              authAPI.logout();
              setUser(null);
            }
            // Jeśli mamy zapisanego użytkownika, zachowaj go
          }
        } catch (error) {
          // Jeśli błąd sieciowy lub serwera, zachowaj zapisanego użytkownika
          console.warn('Błąd weryfikacji tokenu:', error.message);
          
          // Jeśli to błąd autoryzacji (401/403) LUB komunikat o wygasłej sesji
          // i NIE mamy zapisanego użytkownika, wyloguj
          const isAuthError = error.message.includes('401') || 
                             error.message.includes('403') || 
                             error.message.includes('Unauthorized') ||
                             error.message.includes('wygasła') ||
                             error.message.includes('Sesja wygasła');
          
          if (isAuthError) {
            // Jeśli mamy zapisanego użytkownika, zachowaj go (może być problem z siecią)
            // Usuń tylko jeśli nie ma zapisanego użytkownika
            if (!savedUser) {
              console.warn('Token wygasł i brak zapisanego użytkownika - wylogowywanie');
              authAPI.logout();
              setUser(null);
            } else {
              console.warn('Token wygasł, ale zachowuję zapisanego użytkownika (może być problem z siecią)');
              // Zachowaj zapisanego użytkownika
            }
          }
          // W przeciwnym razie (błąd sieci), zachowaj zapisanego użytkownika
        }
      }
      
      setIsCheckingAuth(false);
    };
    
    initializeUser();
  }, []);

  // Ładuj dane z API po zalogowaniu
  useEffect(() => {
    if (user && authAPI.isAuthenticated()) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    if (!authAPI.isAuthenticated()) return;
    
    setLoading(true);
    setError(null);
    try {
      // Ładuj wszystkie dane równolegle
      const [usersData, employeesData, projectsData, shiftsData, contractsData, positionsData] = await Promise.all([
        usersAPI.getAll().catch(() => []),
        employeesAPI.getAll().catch(() => []),
        projectsAPI.getAll().catch(() => []),
        shiftsAPI.getAll().catch(() => ({})),
        contractsAPI.getAll().catch(() => ({})),
        positionsAPI.getAll().catch(() => ({}))
      ]);

      // Konwertuj pracowników na format użytkowników i połącz z użytkownikami
      const employeesAsUsers = employeesData.map(emp => ({
        id: emp.id,
        email: emp.email || null,
        name: `${emp.firstName} ${emp.lastName}`,
        firstName: emp.firstName,
        lastName: emp.lastName,
        role: 'employee',
        projects: emp.project ? [emp.project] : [],
        position: emp.position || '',
        shift: emp.shift || '',
        rotationId: emp.rotationId || null,
        contract: emp.contract || '',
        employmentType: emp.employmentType || '',
        systemUser: emp.systemUser || '',
        leader: emp.leader || '',
        createdAt: emp.createdAt,
        updatedAt: emp.updatedAt
      }));

      // Połącz użytkowników z pracownikami
      const allUsers = [...usersData, ...employeesAsUsers];

      setUsers(allUsers);
      setProjects(projectsData);
      setShifts(shiftsData);
      setContracts(contractsData);
      setPositions(positionsData);
    } catch (err) {
      console.error('Błąd ładowania danych:', err);
      setError('Nie udało się załadować danych. Sprawdź połączenie z serwerem.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (userData) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('handleLogin called with:', userData);
    }
    // Upewnij się, że permissions jest obiektem
    const userWithPermissions = {
      ...userData,
      permissions: userData.permissions || {}
    };
    setUser(userWithPermissions);
    // Zaktualizuj również localStorage, aby mieć aktualne uprawnienia
    localStorage.setItem('user', JSON.stringify(userWithPermissions));
    // Dane zostaną załadowane przez useEffect po ustawieniu user
  };

  const handleLogout = () => {
    authAPI.logout();
    setUser(null);
    setCurrentView('dashboard');
    localStorage.removeItem('currentView');
    setUsers([]);
    setProjects([]);
    setShifts({});
    setContracts({});
    setPositions({});
  };

  const handleAddUser = async (newUser) => {
    try {
      // Jeśli to pracownik (employee), użyj employeesAPI
      if (newUser.role === 'employee') {
        const employeeData = {
          firstName: newUser.firstName || newUser.name?.split(' ')[0] || '',
          lastName: newUser.lastName || newUser.name?.split(' ').slice(1).join(' ') || '',
          email: newUser.email || null,
          position: newUser.position || '',
          role: 'employee',
          project: newUser.projects?.[0] || '',
          shift: newUser.shift || '',
          rotationId: newUser.rotationId ? String(newUser.rotationId) : null,
          contract: newUser.contract || '',
          employmentType: newUser.employmentType || '',
          systemUser: newUser.systemUser || '',
          leader: newUser.leader || ''
        };
        console.log('Tworzenie pracownika:', employeeData); // Debug
        await employeesAPI.create(employeeData);
        await loadAllData();
      } else {
        // Dla innych użytkowników użyj usersAPI
        const userData = {
          email: newUser.email,
          password: newUser.password || 'temp123',
          name: newUser.name || `${newUser.firstName} ${newUser.lastName}`,
          role: newUser.role || 'user',
          projects: newUser.projects || []
        };
        await usersAPI.create(userData);
        await loadAllData();
      }
    } catch (err) {
      console.error('Błąd dodawania użytkownika:', err);
      setError(err.message || 'Nie udało się dodać użytkownika');
      throw err;
    }
  };

  const handleDeleteUser = async (identifier, isEmployee = false) => {
    try {
      // Jeśli to pracownik (przekazano isEmployee = true lub id zamiast email)
      if (isEmployee || typeof identifier === 'number') {
        // identifier to id pracownika
        console.log('Usuwanie pracownika o id:', identifier);
        await employeesAPI.delete(identifier);
        await loadAllData();
        return;
      }
      
      // Dla użytkowników szukamy po email
      const userToDelete = users.find(u => u.email === identifier);
      if (!userToDelete) {
        console.error('Nie znaleziono użytkownika z email:', identifier);
        return;
      }
      
      if (userToDelete.role === 'employee') {
        // Jeśli to pracownik (znaleziony po email), użyj jego id
        if (userToDelete.id) {
          await employeesAPI.delete(userToDelete.id);
        } else {
          // Fallback - szukaj w employees API
          const employees = await employeesAPI.getAll();
          const employee = employees.find(e => e.email === identifier || 
            (e.firstName && e.lastName && `${e.firstName} ${e.lastName}` === userToDelete.name));
          if (employee) {
            await employeesAPI.delete(employee.id);
          }
        }
      } else {
        // Dla innych użytkowników
        if (userToDelete.id) {
          await usersAPI.delete(userToDelete.id);
        }
      }
      await loadAllData();
    } catch (err) {
      console.error('Błąd usuwania użytkownika:', err);
      setError(err.message || 'Nie udało się usunąć użytkownika');
      throw err;
    }
  };

  const handleUpdateUserRole = async (email, newRole) => {
    try {
      const userToUpdate = users.find(u => u.email === email);
      if (!userToUpdate || !userToUpdate.id) return;
      
      await usersAPI.update(userToUpdate.id, { role: newRole });
      await loadAllData();
      
      // Jeśli zmieniamy rolę zalogowanego użytkownika, zaktualizuj też stan
      if (user && user.email === email) {
        setUser({ ...user, role: newRole });
      }
    } catch (err) {
      console.error('Błąd aktualizacji roli użytkownika:', err);
      setError(err.message || 'Nie udało się zaktualizować roli');
      throw err;
    }
  };

  const handleChangePassword = async (email, newPassword) => {
    try {
      const userToUpdate = users.find(u => u.email === email);
      if (!userToUpdate || !userToUpdate.id) return;
      
      await usersAPI.changePassword(userToUpdate.id, newPassword);
    } catch (err) {
      console.error('Błąd zmiany hasła:', err);
      setError(err.message || 'Nie udało się zmienić hasła');
      throw err;
    }
  };

  const handleAddProject = async (projectName) => {
    try {
      if (projects.includes(projectName)) return;
      
      await projectsAPI.create(projectName);
      await loadAllData();
      window.dispatchEvent(new CustomEvent('projects-updated', { detail: projects }));
    } catch (err) {
      console.error('Błąd dodawania projektu:', err);
      
      // Jeśli błąd związany z tokenem, wyświetl szczegółowy komunikat
      if (err.message && (err.message.includes('token') || err.message.includes('wygasł') || err.message.includes('Nieprawidłowy'))) {
        setError('Sesja wygasła. Wyloguj się i zaloguj ponownie, aby kontynuować.');
        // Opcjonalnie: automatyczne wylogowanie po 3 sekundach
        setTimeout(() => {
          if (err.message.includes('wygasł') || err.message.includes('Nieprawidłowy')) {
            handleLogout();
          }
        }, 3000);
      } else {
        setError(err.message || 'Nie udało się dodać projektu');
      }
      throw err;
    }
  };

  const handleDeleteProject = async (projectName) => {
    try {
      await projectsAPI.delete(projectName);
      await loadAllData();
      window.dispatchEvent(new CustomEvent('projects-updated', { detail: projects }));
    } catch (err) {
      console.error('Błąd usuwania projektu:', err);
      setError(err.message || 'Nie udało się usunąć projektu');
      throw err;
    }
  };

  const handleAddShift = async (projectName, shiftName) => {
    try {
      await shiftsAPI.add(projectName, shiftName);
      const updatedShifts = await shiftsAPI.getAll();
      setShifts(updatedShifts);
    } catch (err) {
      console.error('Błąd dodawania zmiany:', err);
      setError(err.message || 'Nie udało się dodać zmiany');
      throw err;
    }
  };

  const handleDeleteShift = async (projectName, shiftName) => {
    try {
      await shiftsAPI.remove(projectName, shiftName);
      const updatedShifts = await shiftsAPI.getAll();
      setShifts(updatedShifts);
    } catch (err) {
      console.error('Błąd usuwania zmiany:', err);
      setError(err.message || 'Nie udało się usunąć zmiany');
      throw err;
    }
  };

  const handleAddContract = async (projectName, contractName) => {
    try {
      await contractsAPI.add(projectName, contractName);
      const updatedContracts = await contractsAPI.getAll();
      setContracts(updatedContracts);
    } catch (err) {
      console.error('Błąd dodawania umowy:', err);
      setError(err.message || 'Nie udało się dodać umowy');
      throw err;
    }
  };

  const handleDeleteContract = async (projectName, contractName) => {
    try {
      await contractsAPI.remove(projectName, contractName);
      const updatedContracts = await contractsAPI.getAll();
      setContracts(updatedContracts);
    } catch (err) {
      console.error('Błąd usuwania umowy:', err);
      setError(err.message || 'Nie udało się usunąć umowy');
      throw err;
    }
  };

  const handleAddPosition = async (projectName, positionName) => {
    try {
      await positionsAPI.add(projectName, positionName);
      const updatedPositions = await positionsAPI.getAll();
      setPositions(updatedPositions);
    } catch (err) {
      console.error('Błąd dodawania stanowiska:', err);
      setError(err.message || 'Nie udało się dodać stanowiska');
      throw err;
    }
  };

  const handleDeletePosition = async (projectName, positionName) => {
    try {
      await positionsAPI.remove(projectName, positionName);
      const updatedPositions = await positionsAPI.getAll();
      setPositions(updatedPositions);
    } catch (err) {
      console.error('Błąd usuwania stanowiska:', err);
      setError(err.message || 'Nie udało się usunąć stanowiska');
      throw err;
    }
  };

  const handleUpdateUser = async (email, updatedData) => {
    try {
      const userToUpdate = users.find(u => u.email === email);
      if (!userToUpdate) return;
      
      if (userToUpdate.role === 'employee' && userToUpdate.id) {
        // Aktualizuj pracownika
        await employeesAPI.update(userToUpdate.id, updatedData);
      } else if (userToUpdate.id) {
        // Aktualizuj użytkownika
        await usersAPI.update(userToUpdate.id, updatedData);
      }
      
      await loadAllData();
      
      // Jeśli aktualizujemy zalogowanego użytkownika, zaktualizuj też stan
      if (user && user.email === email) {
        setUser({ ...user, ...updatedData });
      }
    } catch (err) {
      console.error('Błąd aktualizacji użytkownika:', err);
      setError(err.message || 'Nie udało się zaktualizować użytkownika');
      throw err;
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'admin':
        // Sprawdź uprawnienia - tylko Owner i Admin mogą mieć dostęp lub użytkownik z uprawnieniem menu.admin
        if (!hasMenuAccess(user, 'admin') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} />;
        }
        return <Admin 
          users={users} 
          projects={projects}
          onAddUser={handleAddUser}
          onDeleteUser={handleDeleteUser}
          onUpdateUserRole={handleUpdateUserRole}
          onChangePassword={handleChangePassword}
          onUpdateUser={handleUpdateUser}
          onAddProject={handleAddProject}
          onDeleteProject={handleDeleteProject}
          currentUser={user}
        />;
      case 'admin-permissions':
        // Sprawdź uprawnienia - tylko Owner i Admin mogą mieć dostęp
        if (user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} />;
        }
        return <PermissionsGrid 
          users={users}
          currentUser={user}
          onUpdate={loadAllData}
        />;
      case 'database':
        // Sprawdź uprawnienia - tylko Owner i Admin mogą mieć dostęp lub użytkownik z uprawnieniem menu.database
        if (!hasMenuAccess(user, 'database') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} />;
        }
        return <DatabaseViewer />;
      case 'hr-dodaj':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onUpdateUser={handleUpdateUser}
          onDeleteUser={handleDeleteUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="dodaj"
        />;
      case 'hr-lista':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onUpdateUser={handleUpdateUser}
          onDeleteUser={handleDeleteUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="lista"
        />;
      case 'hr-grafik':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="grafik"
        />;
      case 'hr-grafik2':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="grafik2"
        />;
      case 'hr-absencje':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="absencje"
        />;
      case 'hr-rotacja':
        // Sprawdź uprawnienia do HR
        if (!hasMenuAccess(user, 'hr') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <HR 
          users={users}
          projects={projects}
          shifts={shifts}
          contracts={contracts}
          positions={positions}
          onAddUser={handleAddUser}
          onAddShift={handleAddShift}
          onDeleteShift={handleDeleteShift}
          onAddContract={handleAddContract}
          onDeleteContract={handleDeleteContract}
          onAddPosition={handleAddPosition}
          onDeletePosition={handleDeletePosition}
          currentUser={user}
          initialSection="rotacja"
        />;
      case 'kpi-wgraj':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-wgraj" user={user}>
            <WgrajDane user={user} projects={projects} />
          </KPILayout>
        );
      case 'kpi':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi" user={user}>
            <KPIDashboard />
          </KPILayout>
        );
      case 'kpi-raport':
        if (!hasMenuAccess(user, 'kpi') || !hasKpiPermission(user, 'uzupelnijRaportView')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-raport" user={user}>
            <UzupelnijRaport user={user} />
          </KPILayout>
        );
      case 'kpi-ps':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-ps" user={user}>
            <PS />
          </KPILayout>
        );
      case 'kpi-pick':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-pick" user={user}>
            <Pick />
          </KPILayout>
        );
      case 'kpi-transport':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-transport" user={user}>
            <Transport />
          </KPILayout>
        );
      case 'kpi-forklif':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-forklif" user={user}>
            <ForklifOww />
          </KPILayout>
        );
      case 'kpi-przyjecie':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-przyjecie" user={user}>
            <Przyjecie />
          </KPILayout>
        );
      case 'kpi-inspekt':
        if (!hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-inspekt" user={user}>
            <Inspekt />
          </KPILayout>
        );
      case 'kpi-podsumowanie':
        if (!hasMenuAccess(user, 'kpi') || !hasKpiPermission(user, 'kpiPodsumowanieView')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-podsumowanie" user={user}>
            <KPIPodsumowanie />
          </KPILayout>
        );
      case 'kpi-widget':
        if (user?.role !== 'owner' || !hasMenuAccess(user, 'kpi')) {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return (
          <KPILayout currentView="kpi-widget" user={user}>
            <KPIWidget />
          </KPILayout>
        );
      case 'quality-tablica':
        // Sprawdź uprawnienia do Quality (menu.quality lub quality.view)
        if (!hasMenuAccess(user, 'quality') && !hasQualityPermission(user, 'view') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <Tablica user={user} projects={projects} users={users} openAlertId={openAlertIdForQuality} onOpenAlertHandled={() => setOpenAlertIdForQuality(null)} />;
      case 'quality-dodaj':
        // Sprawdź uprawnienia do Quality (menu.quality lub quality.view)
        if (!hasMenuAccess(user, 'quality') && !hasQualityPermission(user, 'view') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <Tablica user={user} projects={projects} users={users} openAlertId={openAlertIdForQuality} onOpenAlertHandled={() => setOpenAlertIdForQuality(null)} />;
      case 'quality':
        // Sprawdź uprawnienia do Quality (menu.quality lub quality.view)
        if (!hasMenuAccess(user, 'quality') && !hasQualityPermission(user, 'view') && user?.role !== 'owner' && user?.role !== 'admin') {
          return <Dashboard user={user} projects={projects} users={users} />;
        }
        return <Tablica user={user} projects={projects} users={users} openAlertId={openAlertIdForQuality} onOpenAlertHandled={() => setOpenAlertIdForQuality(null)} />;
      case 'dashboard':
      default:
        // Sprawdź uprawnienia do Dashboard
        if (!hasMenuAccess(user, 'dashboard') && user?.role !== 'owner' && user?.role !== 'admin') {
          // Jeśli użytkownik nie ma dostępu do Dashboard, przekieruj do pierwszego dostępnego menu
          if (hasMenuAccess(user, 'quality') || hasQualityPermission(user, 'view')) {
            return <Tablica user={user} projects={projects} users={users} openAlertId={openAlertIdForQuality} onOpenAlertHandled={() => setOpenAlertIdForQuality(null)} />;
          } else if (hasMenuAccess(user, 'kpi')) {
            return (
              <KPILayout currentView="kpi" user={user}>
                <KPIDashboard />
              </KPILayout>
            );
          } else if (hasMenuAccess(user, 'hr')) {
            return <HR 
              users={users}
              projects={projects}
              shifts={shifts}
              contracts={contracts}
              positions={positions}
              onAddUser={handleAddUser}
              onUpdateUser={handleUpdateUser}
              onDeleteUser={handleDeleteUser}
              onAddShift={handleAddShift}
              onDeleteShift={handleDeleteShift}
              onAddContract={handleAddContract}
              onDeleteContract={handleDeleteContract}
              onAddPosition={handleAddPosition}
              onDeletePosition={handleDeletePosition}
              currentUser={user}
              initialSection="lista"
            />;
          } else {
            return <div style={{ padding: '40px', textAlign: 'center' }}>
              <h2>Brak dostępu</h2>
              <p>Nie masz uprawnień do żadnej sekcji aplikacji. Skontaktuj się z administratorem.</p>
            </div>;
          }
        }
        return <Dashboard user={user} projects={projects} users={users} />;
    }
  };

  const isKPIView = ['kpi', 'kpi-raport', 'kpi-ps', 'kpi-pick', 'kpi-transport', 'kpi-forklif', 'kpi-przyjecie', 'kpi-inspekt', 'kpi-podsumowanie', 'kpi-widget'].includes(currentView);

  return (
    <div className={`App${isKPIView ? ' app-theme-kpi-analytics app-fixed-viewport' : ''}`}>
      {error && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          background: '#ff4444', 
          color: 'white', 
          padding: '10px', 
          textAlign: 'center',
          zIndex: 9999
        }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '10px' }}>✕</button>
        </div>
      )}
      {loading && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          background: '#4CAF50', 
          color: 'white', 
          padding: '10px', 
          textAlign: 'center',
          zIndex: 9998
        }}>
          Ładowanie danych...
        </div>
      )}
      {isCheckingAuth ? (
        // Pokazuj loader podczas weryfikacji autentykacji
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontSize: '18px'
        }}>
          Ładowanie...
        </div>
      ) : (
        <div className={`app-content${isKPIView ? ' app-theme-kpi-analytics' : ''}`}>
          {!user ? (
            <LoginAPI onLogin={handleLogin} />
          ) : (
            <>
              <Navbar user={user} onLogout={handleLogout} isFullscreen={isFullscreen} />
              {renderContent()}
            </>
          )}
        </div>
      )}
      <footer className="app-footer">
        © {currentYear} Marcin Plewa. Wszelkie prawa zastrzeżone.
      </footer>
    </div>
  );
}

export default App;
